import React, { useCallback, useEffect, useState } from 'react';
import Track from './components/pages/track';
import TrackingFlow from './components/pages/tracking-flow';
import AdminReview from './components/pages/admin-review';
import {
  lookupShipment,
  readTrackingDeepLink,
  writeTrackingPageUrl,
} from './services/tracking-api';

const DeepLinkLoading = () => (
  <main className="tracking-deep-link-loading" role="status" aria-live="polite">
    <span className="tracking-loading-spinner" aria-hidden="true" />
    <strong>Loading shipment details…</strong>
  </main>
);

function App() {
  const [initialDeepLink] = useState(readTrackingDeepLink);
  const [shipment, setShipment] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(initialDeepLink));
  const [lookupError, setLookupError] = useState('');
  const [isAdminOpen, setIsAdminOpen] = useState(false);

  const handleTrack = useCallback(async (trackingNumber, options = {}) => {
    const { historyMode = 'push', ...lookupOptions } = options;
    const normalizedTrackingNumber = String(trackingNumber || '').trim();

    if (historyMode !== 'none') {
      writeTrackingPageUrl(normalizedTrackingNumber, { mode: historyMode });
    }

    setIsLoading(true);
    setLookupError('');

    try {
      const result = await lookupShipment(normalizedTrackingNumber, lookupOptions);
      setShipment(result);
      if (historyMode !== 'none' && result?.trackingNumber) {
        writeTrackingPageUrl(result.trackingNumber, { mode: 'replace' });
      }
    } catch (error) {
      setShipment(null);
      setLookupError(error?.message || 'Unable to load tracking details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initialDeepLink) return;
    handleTrack(initialDeepLink.trackingNumber, {
      ...initialDeepLink.options,
      historyMode: 'replace',
    });
  }, [handleTrack, initialDeepLink]);

  useEffect(() => {
    const onPopState = () => {
      const deepLink = readTrackingDeepLink();
      if (!deepLink) {
        setShipment(null);
        setLookupError('');
        setIsLoading(false);
        return;
      }

      handleTrack(deepLink.trackingNumber, {
        ...deepLink.options,
        historyMode: 'none',
      });
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [handleTrack]);

  const handleTrackAnother = () => {
    setShipment(null);
    setLookupError('');
    writeTrackingPageUrl(null);
  };

  let page;
  if (shipment) {
    page = (
      <TrackingFlow
        shipment={shipment}
        onTrackAnother={handleTrackAnother}
        onOpenAdmin={() => setIsAdminOpen(true)}
      />
    );
  } else if (initialDeepLink && isLoading) {
    page = <DeepLinkLoading />;
  } else {
    page = (
      <Track
        onTrack={handleTrack}
        isLoading={isLoading}
        apiError={lookupError}
        onClearError={() => setLookupError('')}
        onOpenAdmin={() => setIsAdminOpen(true)}
      />
    );
  }

  return (
    <>
      {page}
      {isAdminOpen ? <AdminReview onClose={() => setIsAdminOpen(false)} /> : null}
    </>
  );
}

export default App;
